"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.planRealtimeMetricDependencies = exports.localMetricRawDependencies = exports.findResolvedLocalMetric = exports.resolveLocalMetrics = void 0;
const metric_catalog_1 = require("./metric-catalog");
const global_formula_catalog_1 = require("./global-formula-catalog");
const model_1 = require("./model");
const MAX_FORMULA_DEPTH = 8;
const MAX_FORMULA_REFERENCES = 16;
const localMetricKey = (id) => `local.${id}`;
const mergeReferences = (...descriptors) => [
    ...new Set(descriptors.flatMap((descriptor) => descriptor.referencedMetricKeys)),
];
const metricDescriptor = (metric) => metric.valueType === 'number' || metric.valueType === 'money'
    ? { valueType: metric.valueType, currency: undefined, referencedMetricKeys: [metric.key] }
    : undefined;
const arithmeticIssue = (path, message) => ({ path, message });
/**
 * Resolves filter-local metrics into validated, typed expression descriptors.
 * The function is pure and intentionally does not compile SQL; the query layer
 * consumes the resolved raw metric dependency set later.
 */
const resolveLocalMetrics = (definition, hasClientScope, allowDeferredMetricReferences = false) => {
    const issues = [];
    const localMetrics = definition.localMetrics ?? [];
    const localsById = new Map();
    const resolutions = new Map();
    const visiting = new Set();
    localMetrics.forEach((localMetric, index) => {
        if (localsById.has(localMetric.id)) {
            issues.push({
                path: `localMetrics[${String(index)}].id`,
                message: `The local metric id must be unique: ${localMetric.id}`,
            });
        }
        else {
            localsById.set(localMetric.id, localMetric);
        }
    });
    const resolveMetricReference = (metricKey, path) => {
        if (metricKey.startsWith('local.')) {
            const localMetricId = metricKey.slice('local.'.length);
            const resolution = resolveLocalMetric(localMetricId, path);
            return resolution?.descriptor;
        }
        const metric = (0, metric_catalog_1.findRealtimeMetric)(metricKey);
        if (metric === undefined) {
            issues.push({ path, message: `Unknown metric: ${metricKey}` });
            return undefined;
        }
        if (!(0, metric_catalog_1.isAuthorableRealtimeMetric)(metric) && !(allowDeferredMetricReferences && metric.availability === 'deferred')) {
            issues.push({ path, message: `The metric is not available yet: ${metricKey}` });
        }
        if (metric.dataScope === 'client' && !hasClientScope) {
            issues.push({ path, message: `The metric requires a client-owned filter: ${metricKey}` });
        }
        const descriptor = metricDescriptor(metric);
        if (descriptor === undefined) {
            issues.push({ path, message: `A formula requires a numeric or money metric: ${metricKey}` });
            return undefined;
        }
        const globalFormula = (0, global_formula_catalog_1.findGlobalFormula)(metricKey);
        return globalFormula === undefined
            ? descriptor
            : {
                ...descriptor,
                referencedMetricKeys: [metricKey, ...globalFormula.inputMetrics],
            };
    };
    const resolveExpression = (expression, path, depth) => {
        if (depth > MAX_FORMULA_DEPTH) {
            issues.push({
                path,
                message: `A local formula may be at most ${String(MAX_FORMULA_DEPTH)} levels deep`,
            });
            return undefined;
        }
        switch (expression.kind) {
            case 'metric':
                return resolveMetricReference(expression.metric, `${path}.metric`);
            case 'number':
                if (!Number.isFinite(expression.value)) {
                    issues.push({ path: `${path}.value`, message: 'A formula number must be finite' });
                    return undefined;
                }
                return { valueType: 'number', currency: undefined, referencedMetricKeys: [] };
            case 'convertCurrency': {
                const descriptor = resolveExpression(expression.expression, `${path}.expression`, depth + 1);
                if (descriptor === undefined)
                    return undefined;
                if (descriptor.valueType !== 'money') {
                    issues.push(arithmeticIssue(path, 'Currency conversion requires a money-valued expression'));
                    return undefined;
                }
                if (descriptor.currency !== undefined) {
                    issues.push(arithmeticIssue(path, 'Currency conversion cannot convert an already converted expression'));
                    return undefined;
                }
                return { ...descriptor, currency: expression.targetCurrency };
            }
            case 'add':
            case 'subtract':
            case 'multiply':
            case 'divide': {
                const left = resolveExpression(expression.left, `${path}.left`, depth + 1);
                const right = resolveExpression(expression.right, `${path}.right`, depth + 1);
                if (left === undefined || right === undefined)
                    return undefined;
                const referencedMetricKeys = mergeReferences(left, right);
                if (expression.kind === 'add' || expression.kind === 'subtract') {
                    if (left.valueType !== right.valueType) {
                        issues.push(arithmeticIssue(path, `${expression.kind} requires operands with the same value type`));
                        return undefined;
                    }
                    if (left.valueType === 'money' && (left.currency === undefined || left.currency !== right.currency)) {
                        issues.push(arithmeticIssue(path, `${expression.kind} requires explicit conversion of both money operands to one currency`));
                        return undefined;
                    }
                    return { valueType: left.valueType, currency: left.currency, referencedMetricKeys };
                }
                if (expression.kind === 'multiply') {
                    if (left.valueType === 'money' && right.valueType === 'money') {
                        issues.push(arithmeticIssue(path, 'Multiplication cannot combine two money-valued expressions'));
                        return undefined;
                    }
                    const money = left.valueType === 'money' ? left : right.valueType === 'money' ? right : undefined;
                    return {
                        valueType: money === undefined ? 'number' : 'money',
                        currency: money?.currency,
                        referencedMetricKeys,
                    };
                }
                if (expression.right.kind === 'number' && expression.right.value === 0) {
                    issues.push(arithmeticIssue(`${path}.right`, 'A formula cannot divide by zero'));
                    return undefined;
                }
                if (right.valueType === 'money' && left.valueType === 'number') {
                    issues.push(arithmeticIssue(path, 'A number cannot be divided by a money-valued expression'));
                    return undefined;
                }
                if (left.valueType === 'money' && right.valueType === 'money') {
                    if (left.currency === undefined || left.currency !== right.currency) {
                        issues.push(arithmeticIssue(path, 'Money division requires explicit conversion to one currency'));
                        return undefined;
                    }
                    return { valueType: 'number', currency: undefined, referencedMetricKeys };
                }
                return {
                    valueType: left.valueType,
                    currency: left.valueType === 'money' ? left.currency : undefined,
                    referencedMetricKeys,
                };
            }
        }
    };
    const resolveLocalMetric = (id, referencePath) => {
        const existing = resolutions.get(id);
        if (existing !== undefined)
            return existing;
        const localMetric = localsById.get(id);
        if (localMetric === undefined) {
            issues.push({ path: referencePath, message: `Unknown local metric: ${localMetricKey(id)}` });
            return undefined;
        }
        if (visiting.has(id)) {
            issues.push({ path: referencePath, message: `A local formula cannot reference itself: ${localMetricKey(id)}` });
            return undefined;
        }
        visiting.add(id);
        const localIndex = localMetrics.indexOf(localMetric);
        const descriptor = resolveExpression(localMetric.expression, `localMetrics[${String(localIndex)}].expression`, 1);
        visiting.delete(id);
        if (descriptor === undefined)
            return undefined;
        if (descriptor.referencedMetricKeys.length === 0) {
            issues.push({
                path: `localMetrics[${String(localIndex)}].expression`,
                message: 'A local formula must reference at least one approved metric',
            });
            return undefined;
        }
        if (descriptor.referencedMetricKeys.length > MAX_FORMULA_REFERENCES) {
            issues.push({
                path: `localMetrics[${String(localIndex)}].expression`,
                message: `A local formula may reference at most ${String(MAX_FORMULA_REFERENCES)} metrics`,
            });
            return undefined;
        }
        const resolution = {
            descriptor,
            expression: localMetric.expression,
            definition: {
                key: localMetricKey(id),
                displayName: localMetric.displayName,
                valueType: descriptor.valueType,
                queryPhases: ['eligibility', 'scoring'],
                supportedOperators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
                availability: 'authorable',
                ...(descriptor.valueType === 'money' ? { currencyPolicy: 'same_currency_only' } : {}),
            },
        };
        resolutions.set(id, resolution);
        return resolution;
    };
    // Resolve every declaration so cycles and invalid unused formulas cannot be saved.
    localMetrics.forEach((localMetric, index) => {
        resolveLocalMetric(localMetric.id, `localMetrics[${String(index)}].id`);
    });
    return { resolutions, issues };
};
exports.resolveLocalMetrics = resolveLocalMetrics;
const findResolvedLocalMetric = (resolutions, metricKey) => metricKey.startsWith('local.') ? resolutions.get(metricKey.slice('local.'.length)) : undefined;
exports.findResolvedLocalMetric = findResolvedLocalMetric;
const dedupe = (keys) => [...new Set(keys)];
const expandGlobalFormulaDependencies = (metricKey, visiting) => {
    const formula = (0, global_formula_catalog_1.findGlobalFormula)(metricKey);
    if (formula === undefined) {
        return { rawMetricKeys: [metricKey], globalFormulaKeys: [] };
    }
    if (visiting.has(metricKey)) {
        throw new Error(`Global formula catalog contains a cycle at ${metricKey}`);
    }
    const nextVisiting = new Set(visiting).add(metricKey);
    const dependencies = formula.inputMetrics.map((inputMetric) => expandGlobalFormulaDependencies(inputMetric, nextVisiting));
    return {
        rawMetricKeys: dedupe(dependencies.flatMap((dependency) => dependency.rawMetricKeys)),
        globalFormulaKeys: dedupe([metricKey, ...dependencies.flatMap((dependency) => dependency.globalFormulaKeys)]),
    };
};
const expandMetricDependencies = (metricKey, localMetrics) => {
    const localMetric = (0, exports.findResolvedLocalMetric)(localMetrics, metricKey);
    if (localMetric === undefined) {
        const globalDependencies = expandGlobalFormulaDependencies(metricKey, new Set());
        return { ...globalDependencies, localMetricKeys: [] };
    }
    const dependencies = localMetric.descriptor.referencedMetricKeys.map((dependency) => expandGlobalFormulaDependencies(dependency, new Set()));
    return {
        rawMetricKeys: dedupe(dependencies.flatMap((dependency) => dependency.rawMetricKeys)),
        globalFormulaKeys: dedupe(dependencies.flatMap((dependency) => dependency.globalFormulaKeys)),
        localMetricKeys: [metricKey],
    };
};
/** Returns the raw source leaves needed by one already resolved local formula. */
const localMetricRawDependencies = (localMetric) => dedupe(localMetric.descriptor.referencedMetricKeys.flatMap((metricKey) => expandGlobalFormulaDependencies(metricKey, new Set()).rawMetricKeys));
exports.localMetricRawDependencies = localMetricRawDependencies;
const currencyConversionTargets = (metricKeys, localMetrics) => {
    const targets = new Set();
    const visitedLocalMetricKeys = new Set();
    const visitExpression = (expression) => {
        switch (expression.kind) {
            case 'number':
                return;
            case 'convertCurrency':
                targets.add(expression.targetCurrency);
                visitExpression(expression.expression);
                return;
            case 'add':
            case 'subtract':
            case 'multiply':
            case 'divide':
                visitExpression(expression.left);
                visitExpression(expression.right);
                return;
            case 'metric': {
                const localMetric = (0, exports.findResolvedLocalMetric)(localMetrics, expression.metric);
                if (localMetric !== undefined && !visitedLocalMetricKeys.has(expression.metric)) {
                    visitedLocalMetricKeys.add(expression.metric);
                    visitExpression(localMetric.expression);
                }
            }
        }
    };
    metricKeys.forEach((metricKey) => {
        const localMetric = (0, exports.findResolvedLocalMetric)(localMetrics, metricKey);
        if (localMetric !== undefined && !visitedLocalMetricKeys.has(metricKey)) {
            visitedLocalMetricKeys.add(metricKey);
            visitExpression(localMetric.expression);
        }
    });
    return [...targets];
};
/** Plans only catalog-owned global KPI expansion; local SQL compilation follows in a later slice. */
const planRealtimeMetricDependencies = (definition, options = {}) => {
    const includeScoreRules = options.includeScoreRules ?? true;
    const localMetrics = (0, exports.resolveLocalMetrics)(definition, options.hasClientScope ?? false, options.allowDeferredMetricReferences ?? false).resolutions;
    const referencedMetricKeys = dedupe([
        ...(0, model_1.conditionMetricKeys)(definition.eligibility),
        ...(includeScoreRules ? definition.scoreRules.flatMap((rule) => (0, model_1.conditionMetricKeys)(rule.when)) : []),
    ]);
    const dependencies = referencedMetricKeys.map((metricKey) => expandMetricDependencies(metricKey, localMetrics));
    return {
        referencedMetricKeys,
        rawMetricKeys: dedupe(dependencies.flatMap((dependency) => dependency.rawMetricKeys)),
        globalFormulaKeys: dedupe(dependencies.flatMap((dependency) => dependency.globalFormulaKeys)),
        localMetricKeys: dedupe(dependencies.flatMap((dependency) => dependency.localMetricKeys)),
        currencyConversionTargets: currencyConversionTargets(referencedMetricKeys, localMetrics),
    };
};
exports.planRealtimeMetricDependencies = planRealtimeMetricDependencies;
//# sourceMappingURL=formula-resolution.js.map